// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

/**
 * Thousands-separator helpers for the fact-find wizard's monetary amount inputs
 * (`money` amount + `currency`). These fields display grouped — 3,000 not 3000 —
 * while the value stored/emitted upward stays a plain `number`.
 *
 * Grouping only ever touches the integer part, so values below 1,000 and the
 * decimal portion are rendered verbatim. Non-money numeric fields (ages, years,
 * percentages, terms) deliberately do NOT use these — e.g. a 4-digit year like
 * 2024 must never read as 2,024.
 */

/**
 * Group the integer part of a raw — possibly partial, mid-typing — numeric
 * string with commas, preserving an in-progress trailing `.` and any decimals
 * exactly as typed. Existing commas are stripped first so re-formatting is
 * idempotent. Empty input → ''.
 */
export function groupDigits(raw: string): string {
  const cleaned = raw.replace(/,/g, '');
  if (cleaned === '') return '';
  const dot = cleaned.indexOf('.');
  const intPart = dot === -1 ? cleaned : cleaned.slice(0, dot);
  const decPart = dot === -1 ? '' : cleaned.slice(dot + 1);
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return dot === -1 ? groupedInt : `${groupedInt}.${decPart}`;
}

/**
 * Parse a (possibly comma-grouped) string to a `number`, or `undefined` when
 * blank/invalid — same contract as the wizard's existing `numberOrUndefined`,
 * so the stored answer is always a plain number.
 */
export function parseAmount(text: string): number | undefined {
  const cleaned = text.replace(/,/g, '').trim();
  if (cleaned === '') return undefined;
  const n = Number(cleaned);
  return Number.isNaN(n) ? undefined : n;
}

/**
 * Format a stored numeric value for initial display in a grouped input. No
 * forced decimal padding (3000 → "3,000", never "3,000.00"). Non-numbers → ''.
 */
export function formatAmount(value: unknown): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return groupDigits(String(value));
}
